import { memo, useState, type SyntheticEvent } from 'react'
import { KIND_WORD, alsoOf, clipDescription, detailsOf, languageOf, lengthOf, priceOf, timeOf, type DeskShowing } from '../lib/deskBrowse'
import { heroFallbackUrl, heroIsVenue, heroUrl, posterUrl } from '../lib/entries'
import { setPlace } from '../lib/rowMeta'
import { placeIdOf } from '../lib/places'
import { VenueLink } from '../components/VenueLink'
import type { FeedRow } from '../lib/types'


/** A picture that is of the thing: a film's poster, or the row's own picture.
 *  The venue photograph that sixty concerts share is none of them (W4 rules). */
export function ownPicture(row: FeedRow, kind: string, size: 800 | 1600 = 800): string | null {
  if (kind === 'film') return posterUrl(row, size === 800 ? 'w185' : 'w500')
  return heroIsVenue(row) ? null : heroUrl(row, size)
}

/** A rendition not yet written falls back to the original, once; a picture
 *  that still will not load is `onGone`'s to give up on. */
export const toOriginal = (row: FeedRow, onGone?: () => void) => (e: SyntheticEvent<HTMLImageElement>) => {
  const orig = heroFallbackUrl(row)
  const img = e.currentTarget
  if (orig && img.src !== orig) img.src = orig
  else onGone?.()
}

/** The DOM id of a row, for `aria-activedescendant` and the arrow keys. */
export const rowDomId = (id: string) => `br-row-${id}`

export interface BrowseRowProps {
  row: FeedRow
  kind: string
  today: string
  /** Already judged a picture of this thing, or null. */
  pic: string | null
  description: string | null
  showings: DeskShowing[]
  selected: boolean
  /** The one row in the list that takes Tab (roving tabindex, a4-18). */
  tabbable: boolean
  hidden: boolean
  saved: boolean
  followed: boolean
  onSelect: (id: string) => void
  onSave: (row: FeedRow) => void
  onHide: (row: FeedRow) => void
}

function RowImpl({ row, kind, today, pic, description, showings, selected, tabbable, hidden, saved, followed, onSelect, onSave, onHide }: BrowseRowProps) {
  const [gone, setGone] = useState(false)
  const d = detailsOf(row, kind)
  // Cards WP4: the phone card's place, from the same function — the set's
  // cinemas named or counted, else the row's own venue as before.
  const set = setPlace(row)
  // `alsoOf` leaves the venue off a night at the row's own venue, which is
  // right when the side column names that venue. Beside "4 cinemas" it is
  // not named anywhere, so a set's nights all carry theirs.
  const also = alsoOf(set ? { ...row, venue: null } : row, showings, today)
  const where = set ?? row.venue
  // The district is the canonical's own venue's, so beside "10 cinemas" it
  // read as where all ten are. A set has no one district; it goes.
  const district = set ? null : row.district
  const desc = clipDescription(description, 320)
  const orig = row.film?.original_title ?? row.title_original
  const mins = lengthOf(row)
  const ml = [priceOf(row), languageOf(row), d.shape !== 'film' && d.shape !== 'exhibition' && mins ? `${mins} min` : ''].filter(Boolean)
  // A film keeps its poster column even without a poster, so a list of films
  // does not shift column from row to row; every other kind draws media only
  // where it has a picture of its own.
  const media = d.shape === 'film' ? 'film' : pic && !gone ? 'wide' : null
  const cls = ['br-row', media, selected && 'sel', hidden && 'hid'].filter(Boolean).join(' ')

  return (
    <div className={cls} id={rowDomId(row.id)} data-id={row.id} role="option" aria-selected={selected}
      tabIndex={tabbable ? 0 : -1} onClick={() => onSelect(row.id)}
      onKeyDown={e => {
        if (e.target !== e.currentTarget || (e.key !== 'Enter' && e.key !== ' ')) return
        e.preventDefault()
        onSelect(row.id)
      }}>
      <div className={`t${d.shape === 'exhibition' ? ' run' : ''}`}>{timeOf(row, kind)}</div>
      {media === 'film' && <div className="po">{pic && !gone && <img src={pic} alt="" loading="lazy" onError={toOriginal(row, () => setGone(true))} />}</div>}
      {media === 'wide' && <div className="pw"><img src={pic!} alt="" loading="lazy" onError={toOriginal(row, () => setGone(true))} /></div>}
      <div className="mid">
        <div className="br-ttl">
          {row.title}
          {/* A space, not only a margin: without one the title and the
              original ran as one unbreakable word past the Save box at 960. */}
          {d.shape === 'film' && orig && orig !== row.title && <> <small>{orig}</small></>}
        </div>
        {/* a3-23 (Doug chose: the venue under the title). In the right-hand
            column it sat a thousand pixels from a short title at 1920. */}
        {(where || district || followed) && (
          <div className="where">
            {where && <span className="vn">{/* a1-28: the place page, its own click — the row's is "select". */}{!set && placeIdOf(row) ? <VenueLink id={placeIdOf(row)!}>{where}</VenueLink> : where}</span>}
            {(district || followed) && <span className="ds">{[district, followed ? 'following' : ''].filter(Boolean).join(' · ')}</span>}
          </div>
        )}
        {d.who && (d.shape === 'night' ? <div className="line"><b>{d.who}</b></div> : <div className="who">{d.who}</div>)}
        {d.shape === 'programme' && d.programme.length > 0 && (
          <ul className="prog">
            {d.programme.map((p, i) => <li key={i}>{p.composer && <b>{p.composer} </b>}{p.work}</li>)}
          </ul>
        )}
        {d.facts && (d.shape === 'film' || d.shape === 'night' || d.shape === 'exhibition') && <div className="facts">{d.facts}</div>}
        {desc && <div className="desc">{desc}</div>}
        {also && <div className="also"><b>Also</b> {also}</div>}
      </div>
      <div className="side">
        <div className="kd">{KIND_WORD[kind] ?? kind}</div>
        {ml.length > 0 && <div className="ml">{ml.map(x => <span key={x}>{x}</span>)}</div>}
      </div>
      <div className="ac">
        <button type="button" className={saved ? 'on' : ''} aria-pressed={saved}
          onClick={e => { e.stopPropagation(); onSave(row) }}>{saved ? 'Saved' : 'Save'}</button>
        <button type="button" className={hidden ? 'on' : ''} aria-pressed={hidden}
          onClick={e => { e.stopPropagation(); onHide(row) }}>{hidden ? 'Hidden' : 'Not for me'}</button>
      </div>
    </div>
  )
}

export const BrowseRow = memo(RowImpl)
